import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { CommonFunctionsService, DatastoreService } from '../../services';
import { EXTENSION_ITEM_DESCRIPTIONS } from '../../services/extend-state.service';

/**
 * True when one of the booking's extra-item links resolves to an extension
 * item ("Extended ceremony time (30/60 minutes)"). Descriptions are resolved
 * from the service extras by item_id — the link's own data.item is free text
 * that can drift (same pattern as My Add-ons). Catches bookings extended
 * without a settings.extended_at stamp (RWRSPI-1010).
 */
export function linksIncludeExtensionItem(
  links: any[],
  serviceExtraItems: any[]
): boolean {
  if (!Array.isArray(links) || !Array.isArray(serviceExtraItems)) {
    return false;
  }
  return links.some((link: any) => {
    const match = serviceExtraItems.find(
      (ei: any) => ei.item_id === link.item_id
    );
    return EXTENSION_ITEM_DESCRIPTIONS.includes(match?.item_description ?? '');
  });
}

/** The bespoke tiers a ceremony-time extension sells as (case-insensitive). */
export const BESPOKE_ITEM_NAMES: readonly string[] = ['bespoke plus', 'bespoke extra'];

/**
 * True when one of the booking's links is a "Bespoke Plus" / "Bespoke Extra"
 * item by NAME — matched on the catalogue-resolved item_description or the
 * link's own data.item. Complements linksIncludeExtensionItem for links that
 * can't be resolved against the booked service's catalogue: extension links
 * are created under the booking's write service id (the catalogue id + 1,
 * e.g. 48376 for the Westminster Room's 48375), so their item_id may not
 * exist in the extras the caller fetched. Used by the First toast bespoke
 * exemption only — NOT by the already-extended guard, so a package-sold
 * bespoke booking isn't silently blocked from the extend journey.
 */
export function linksIncludeBespokeNamedItem(
  links: any[],
  serviceExtraItems: any[]
): boolean {
  if (!Array.isArray(links)) {
    return false;
  }
  const extras = Array.isArray(serviceExtraItems) ? serviceExtraItems : [];
  const isBespokeName = (value: any) =>
    BESPOKE_ITEM_NAMES.includes(String(value ?? '').trim().toLowerCase());
  return links.some((link: any) => {
    const match = extras.find((ei: any) => ei.item_id === link.item_id);
    return isBespokeName(match?.item_description) || isBespokeName(link?.data?.item);
  });
}

/**
 * Blocks the extend routes when the booking has already been extended. A
 * ceremony may only be extended once — the extend flow stamps
 * settings.extended_at on success (RWRSPI-970), which also hides the Extend
 * button on our-ceremony (alreadyExtended). Bookings extended without the
 * stamp are caught by dataStore.bookingHasExtensionItem, computed by
 * our-ceremony from the booking's extra-item links (RWRSPI-1010). This guard
 * stops users bypassing the hidden button via URL bar / bookmark / back.
 *
 * NOT applied to the `confirmation` route: the successful-extend redirect lands
 * there after extended_at has just been stamped, so gating it would bounce the
 * user off their own confirmation page.
 *
 * If there's no booking in the datastore yet, defer to extendRequiresBookingGuard.
 */
export const extendNotExtendedGuard: CanActivateFn = () => {
  const router = inject(Router);
  const dataStore = inject(DatastoreService);
  const commonFunctionsService = inject(CommonFunctionsService);

  const booking = dataStore.booking;
  if (!booking) {
    return true;
  }

  if (!booking?.settings?.extended_at && !dataStore.bookingHasExtensionItem) {
    return true;
  }

  router.navigate([
    `${commonFunctionsService.getCompanyName()}/${commonFunctionsService.getCompanyId()}/our-ceremony`,
  ]);
  return false;
};
